<template>
  <div class="ba-trend"
       :class="`device--${journeyOptions.device}`"
       v-if="journeyOptions">
    <ba-trend-pane-control v-show="nodesDataArray && !noData"
                           :paneScale="Math.round(paneScale * 100)"
                           :sizeControlMin="sizeControlMin"
                           :sizeControlMax="sizeControlMax"
                           :grouping="grouping"
                           :group="group"
                           :groupItems="displayGroupItems"
                           :nodeWidth="nodeWidth"
                           :nodeHeight="nodeHeight"
                           :connectionStrength="connectionStrength"
                           :visibleOptions="visibleOptions"
                           :isThruHighlighting="!!thruData"
                           :isCustomNode="isCustomNode"
                           @onPaneScaleChange="onPaneScaleChange"
                           @onPaneScaleFit="onPaneScaleFit"
                           @onChangeGrouping="setGrouping"
                           @onGroupChange="onGroupChange"
                           @onNodeWidthChange="setNodeWidth"
                           @onNodeHeightChange="setNodeHeight"
                           @onConnectionOptionChange="setConnectionOption"
                           @onVisibleOptionChange="setVisibleOption"
                           @onDownloadExcel="onDownloadExcel" />
    <div class="pane-wrap"
         :class="{ 'node-reporting': nodeReporting }"
         ref="paneWrap"
         v-if="nodesDataArray && !noData">

      <div class="pane"
           ref="pane"
           :style="{
             minWidth: `calc(100% + ${paneExtraPadding[3]}px)`,
             minHeight: `calc(100% + ${paneExtraPadding[0]}px)`,
             padding: `${panePadding[0] + paneExtraPadding[0]}px ${panePadding[1] + paneExtraPadding[1]}px ${panePadding[2] + paneExtraPadding[2]}px ${panePadding[3] + paneExtraPadding[3]}px`,
             transform: `translate3d(${panePosition[0]}px, ${panePosition[1]}px, 0) scale(${paneScale})`
           }">
        <ba-node-list v-if="paneWrapSize[0]"
                      :nodesData="nodesData"
                      :nodesDataArray="nodesDataArray"
                      :thruData="thruData"
                      :thruDataArray="thruDataArray"
                      :paneScale="paneScale"
                      :paneWrapSize="paneWrapSize"
                      :panePosition="panePosition"
                      :panePadding="panePadding"
                      :paneExtraPadding="paneExtraPadding"
                      :moreDataIndex="moreDataIndex"
                      :nodeControlWidth="nodeWidth"
                      :nodeControlHeight="nodeHeight"
                      :sizeControlMin="sizeControlMin"
                      :sizeControlMax="sizeControlMax"
                      :connectionStrength="connectionStrength"
                      :reportShowing="reportShowing"
                      :nodeReporting="nodeReporting"
                      :grouping="grouping"
                      :group="group"
                      :isTrackable="isTrackable"
                      :tracking="trackingData"
                      :visibleOptions="visibleOptions"
                      :benchmark-data="benchmarkData"
                      :isCustomNode="isCustomNode"
                      :isCustomNodeAvailable="isCustomNodeAvailable"
                      :isCustomDisabled="isCustomDisabled"
                      :customRegDate="customRegDate"
                      :domainSubCategory="domainSubCategory"
                      :domainCategory="domainCategory"
                      :overallSessionData="overallSessionData"
                      @onCustomTag="onCustomTag"
                      @onLoadMoreData="loadMoreData"
                      @expandSequence="expandSequence"
                      @onChangeInitialNodeSize="setNodeHeight"
                      @onNodeListResize="setPaneOverflowPosition"
                      @onHighlightNode="loadThruTrafficData"
                      @onRemoveHighlightNode="removeThruTrafficData"
                      @onShowReport="showReport"
                      @onShowCategorySelector="isCategorySelectorOpened = true"
                      @onShowTrackingLayer="showTrackingLayer"
                      @setTracking="setTracking" />
      </div>
    </div>

    <transition name="grouping">
      <grouping class="grouping-pane"
                v-if="grouping && nodesData && !noData"
                :group="group"
                :groupItems="displayGroupItems"
                :domain="''"
                @onGroupChange="onGroupChange" />
    </transition>

<!--    <transition v-if="!noData"-->
<!--                name="fade">-->
<!--      <div class="reliability" v-if="isSampling">-->
<!--        <i class="icon icon&#45;&#45;alarm"></i>-->
<!--        <span v-if="!isSurvey">{{$t('resample.info', {percent: originTrendData.reliability })}}</span>-->
<!--        <span v-else>{{$t('resample.survey', {percent: domain.samplingRate})}}</span>-->
<!--      </div>-->
<!--    </transition>-->

    <transition v-if="!noData" name="fade">
      <div class="pane-base-info" v-if="nodesData || samplingReliabilityText">
        <div class="sampling-reliability" v-if="samplingReliabilityText">
          <div class="icon-wrap">
            <i class="icon icon--bell-white size-16"></i>
          </div>
          <div class="message">{{ samplingReliabilityText }}</div>
        </div>
        <div class="pane-date" v-if="nodesData">
          {{ $t('journey.basedOn', { date: dataTimeString, timezone: timezone }) }}
        </div>
      </div>
    </transition>
    <transition v-if="!noData" name="fade">
      <button class="ai-report-button" :disabled="!ableToShowAiReport" @click="onClickAiReport">
        <i class="icon icon--ai-blue" />
        <span class="text-contents">Report</span>
      </button>
    </transition>

    <transition name="fade">
      <div class="pane-no-data"
           v-if="noData">
        <i class="icon icon--alert"></i><br>
        <h3 v-html="$t('trend.noData.title')"></h3>
        <p v-if="!noResampleData" v-html="$t('trend.noData.description')"></p>
        <p v-else v-html="$t('trend.noData.noResampleDescription')"></p>
      </div>
    </transition>

    <dimmed v-if="noBeforeData">
      <alert :title="$t('trend.noBeforeData.title')"
             :description="$t('trend.noBeforeData.description')"
             :submit="$t('common.confirm')"
             @onSubmit="noBeforeData = false" />
    </dimmed>

    <transition name="node-report"
                @afterLeave="onNodeReportLeave">
      <div class="node-report"
           v-if="reportShowing">
        <ba-node-report v-if="reportData._id !== '-1'"
                        :pageData="reportData"
                        :defaultThumbnail="defaultThumbnail"
                        :captureProcessing="captureProcessing"
                        :valueRange="valueRange"
                        :originData="originTrendData"
                        :timezoneName="domain.timezoneName"
                        :group="grouping ? group : false"
                        :isCustomNode="isCustomNode"
                        :isTracking="Boolean(trackingData)"
                        @onCapture="onCapture" />
        <ba-node-report-etc v-else
                            :originData="originTrendData"
                            :isCustomNode="isCustomNode"
                            :pageData="reportData" />
        <button class="close-report"
                @click="hideReport">
          <i class="icon icon--close-arrow-right"></i>
        </button>
      </div>
    </transition>

    <transition name="fade">
      <dimmed v-if="trackingLayerShowing">
        <ba-tracking-layer :nodeData="trackingNodeData" @close="trackingLayerShowing=false" @setTracking="setTracking" />
      </dimmed>
    </transition>

    <div v-if="partiallyLoading || reportShowing"
         class="blocker"
         @click="onBlockerClick" />

    <transition name="fade">
      <div class="partially-loading"
           v-if="partiallyLoading">
        <ba-loading :applyBackground="true"
                    :showLoadingText="true" />
      </div>
    </transition>

    <dimmed v-if="capturingPage">
      <ba-capture :pageData="capturingPage"
                  :capturingDevice="capturingDevice"
                  :wholeCaptureProcessing="wholeCaptureProcessing"
                  @onWholeCaptureProcessingChange="wholeCaptureProcessing = $event"
                  @onCaptureProcessing="onCaptureProcessing($event)"
                  @onCloseLayer="capturingPage = null" />
    </dimmed>

    <dimmed v-if="movingGroup">
      <alert :title="$t('group.alert.enter.title')"
             :description="$t('group.alert.enter.description')"
             :cancel="$t('common.cancel')"
             :submit="$t('common.confirm')"
             @onSubmit="onSettingGroup"
             @onCancel="movingGroup = false" />
    </dimmed>
    <dimmed v-if="isCategorySelectorOpened">
      <category-selector :matched="domain.AICategory2" :selected="domainSubCategory" :category-list="categoryList" @close="isCategorySelectorOpened = false" @select="changeCategory"/>
    </dimmed>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { format } from 'date-fns'

import {
  getS3Url,
  getS3CaptureDatetime,
  simpleUrl,
  calculateLogDateTime,
  getOffset, getResampleReliability, getDomainForView
} from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import { getData, getMoreData, getThruTrafficData, getNodeDetail, getEtcUrls } from '@/api/trend'
import { getReliabilityData } from '@/api/journey'
import { checkCaptureExist } from '@/api/capture'
import { getTrendBenchmark, getAiCategoryList } from '@/api/ai'
import { getGroupList } from '@/api/group'
import Excel from '@/libs/excel-export'

import Alert from '@/components/ui/Alert'
import Dimmed from '@/components/ui/Dimmed'
import BaLoading from '@/components/ui/ba/Loading'
import BaNodeReport from '@/components/journey/NodeReport'
import BaNodeReportEtc from '@/components/trend/NodeReportEtc'
import BaTrendPaneControl from '@/components/trend/PaneControl'
import BaNodeList from '@/components/trend/NodeList'
import CategorySelector from '@/components/trend/CategorySelector'

import BaCapture from '@/components/journey/Capture'
import BaTrackingLayer from '@/components/journey/TrackingLayer'
import Grouping from '@/components/journey/Grouping'
import { AI_CATEGORY } from '@/libs/constants'

const wheelEventName = 'onwheel' in document ? 'wheel' : 'DOMMouseScroll' in document ? 'DOMMouseScroll' : 'mousewheel'

function getEventPoint (e) {
  if (e.touches) {
    e = e.touches[0] || e.changedTouches[0]
  }
  return [e.pageX || e.clientX, e.pageY || e.clientY]
}

function sortByValue (a, b) {
  return a - b
}

function isEmptyObject (obj) {
  for (let key in obj) {
    return false
  }
  return true
}

export default {
  name: 'BaTrend',
  components: {
    Alert,
    Dimmed,
    BaLoading,
    BaNodeReport,
    BaNodeReportEtc,
    BaTrendPaneControl,
    BaNodeList,
    Grouping,
    BaTrackingLayer,
    BaCapture,
    CategorySelector
  },
  data () {
    return {
      categoryNameList: AI_CATEGORY,
      domainSubCategory: null,
      benchmarkData: null,
      isCategorySelectorOpened: false,
      partiallyLoading: false,

      movingGroup: false,

      nodesData: null,
      nodesDataArray: null,
      moreDataIndex: 0,
      thruData: null,
      thruDataArray: null,
      thruDataCache: {},

      noData: false,
      noResampleData: false,
      noBeforeData: false,

      sizeControlMin: 0,
      sizeControlMax: 100,

      paneScale: 1,
      panePosition: [0, 0],
      paneWrapSize: [0, 0],
      panePadding: [108, 40, 20, 80],
      paneExtraPadding: [170, 190, 140, 150],
      nodeWidth: 0,
      nodeHeight: 0,

      grouping: false,
      group: '1',
      groupItems: {},

      overallSessionData: {
        sessCnt: 0,
        memberSessCnt: 0
      },
      trackingData: null,
      trackingLayerShowing: false,
      trackingNodeData: null,

      nodeReporting: false,
      reportShowing: false,
      reportData: null,

      defaultThumbnail: null,
      capturingPage: null,
      capturingDevice: null,
      captureProcessing: null,
      captureProcessingTimer: null,

      connectionStrength: 50,

      valueRange: {
        transition: [],
        rollback: [],
        dropOff: [],
        sessionTime: [],
        repeat: []
      },
      visibleOptions: {
        dropOff: true,
        rollback: false,
        refresh: false,
        duration: false
      },
      wholeCaptureProcessing: [],
      samplingReliabilityText: null,
      categoryList: null
    }
  },
  computed: {
    ...mapState([
      'journeyPeriod',
      'journeyOptions',
      'gnbMinimized',
      'domain',
      'originTrendData',
      'basicInfo',
      'isSampling'
    ]),
    isCustomNode () {
      return this.journeyOptions.isCustomNode
    },
    isCustomNodeAvailable () {
      if (!this.customRegDate || this.journeyOptions.matchType === 'exact') {
        return false
      }
      return !(this.customRegDate > this.journeyPeriod.start && this.customRegDate > this.journeyPeriod.end)
    },
    isCustomDisabled () {
      if (this.isCustomNodeAvailable) {
        return this.journeyPeriod.start < this.customRegDate && this.customRegDate <= this.journeyPeriod.end
      } else {
        return true
      }
    },
    customRegDate () {
      const beuCustomRegDateStr = this.domain.beuCustomRegDate ? format(this.domain.beuCustomRegDate, 'YYYY-MM-DD') : null
      return beuCustomRegDateStr
    },
    device () {
      return this.journeyOptions.device
    },
    isSurvey () {
      return this.journeyOptions.survey
    },
    isTrackable () {
      const customUserId = this.domain.beuCustomUserId
      return customUserId && customUserId.length > 0
    },
    hasAiReferrerFilter () {
      return this.journeyOptions.aiRefererIds && this.journeyOptions.aiRefererIds.length > 0
    },
    shouldReserveStartContextSpace () {
      return this.isTrackable || this.hasAiReferrerFilter
    },
    timezone () {
      return getOffset(this.domain.timezoneName)
    },
    trackingGetDataMode () {
      if (!this.isTrackable) return null
      if (this.trackingData) return 'count'
      return 'portion'
    },
    trackingDataOption () {
      if (!this.isTrackable) return {}
      if (!this.trackingData) return { isMemberDisplayed: true }

      const { type, isFromFunnel = false, isAllSelected, excludedMembers = [], selectedMembers = [], memberCacheKey, sessionCacheKey } = this.trackingData
      const data = {
        isMemberDisplayed: true
      }

      if (type === 'member') {
        data.memberCacheKey = memberCacheKey
        data.isAllMemberSelected = isAllSelected && isFromFunnel
        data.excludedMembers = excludedMembers.map(item => item.id)
        data.selectedMembers = isAllSelected ? [] : selectedMembers.map(item => item.id)
      } else {
        data.isAllNonMemberSelected = isAllSelected
        data.sessionCacheKey = sessionCacheKey
      }

      return data
    },
    dataOption () {
      return {
        device: this.journeyOptions.device,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath,
        serviceType: 'ba',
        matchType: this.journeyOptions.matchType,
        exist: this.journeyOptions.exist,
        refer: this.journeyOptions.refer,
        aiRefererIds: this.journeyOptions.aiRefererIds,
        utm: this.journeyOptions.utm,
        survey: this.journeyOptions.survey,
        groupId: this.group || 1,
        isCustomNode: this.journeyOptions.isCustomNode,
        ...this.trackingDataOption
      }
    },
    sid () {
      return this.$route.query.sid
    },
    dataTimeString () {
      if (this.nodesData && this.domain) {
        // const loadedAt = this.nodesData.loadedAt
        const loadedAt = calculateLogDateTime(this.domain)
        const formatText = this.$t('journey.basedOnFormat')
        return format(loadedAt, formatText)
      } else {
        return ''
      }
    },
    domainCategory () {
      if (!this.domainSubCategory || !this.categoryList) return null
      const category = Object.entries(this.categoryList).find(([, category]) => category.subcategories && this.domainSubCategory in category.subcategories)
      return category ? { key: category[0], ...category[1] } : null
    },
    displayGroupItems () {
      const items = {}
      for (const [groupId, groupName] of Object.entries(this.groupItems)) {
        items[groupId] = groupName === 'AUTO' ? this.$t('group.automatic') : groupName
      }
      return items
    },
    ableToShowAiReport () {
      return (!this.domain.shared || this.domain.shared === 'share') && !this.journeyOptions.isCustomNode && !this.trackingData && this.journeyOptions.device
    }
  },
  watch: {
    gnbMinimized () {
      setTimeout(() => {
        this.onResize(true)
      }, 500)
    },
    trackingData: {
      handler (newValue) {
        EventBus.$emit('tracking-state', newValue !== null)
      },
      immediate: true
    }
  },
  mounted () {
    const minimumDate = new Date(this.journeyOptions.sid === '7396cc2bb5' ? '2021-10-01' : '2022-01-01')

    getAiCategoryList()
      .then(res => {
        this.categoryList = res
      })
      .catch(e => console.log(e))

    EventBus.$onSticky('loadPaneData', async () => {
      const startDate = new Date(this.journeyPeriod.start)
      if (startDate.getTime() >= minimumDate.getTime()) {
        if (this.journeyOptions.matchType !== 'exact') {
          const AICategory2List = this.domain.AICategory2
          if (AICategory2List && AICategory2List.length > 0) {
            this.domainSubCategory = AICategory2List[0]
          }
          await this.getBenchmarkData()
        }
        this.loadData()
        this.getReliability()
        if (this.noBeforeData) {
          EventBus.$emitSticky('loadPaneNoData', false)
          this.noBeforeData = false
        }
        this.reportShowing = false
        this.panePadding[3] = this.shouldReserveStartContextSpace ? 315 : 80
        this.panePosition = [-this.paneExtraPadding[3], -this.paneExtraPadding[0]]
      } else {
        EventBus.$emitSticky('loadPaneNoData', true)
        this.noBeforeData = true
        this.reportShowing = false
      }
      EventBus.$clearSticky('loadPaneData')
    })

    EventBus.$onSticky('loadPaneNoData', (value, noResampleData) => {
      const startDate = new Date(this.journeyPeriod.start)
      if (startDate.getTime() < minimumDate.getTime()) {
        this.noBeforeData = true
      }
      this.hideReport()
      EventBus.$emit('onLoading', false)
      this.noData = value
      if (noResampleData !== undefined) {
        this.noResampleData = noResampleData
      }
      this.setGlobalNoData(this.noData)
      EventBus.$clearSticky('loadPaneNoData')
    })

    EventBus.$on('journeyOptionChange', () => {
      this.trackingData = null
    })

    EventBus.$on('settingGroup', (value = true) => {
      this.movingGroup = value
    })

    EventBus.$on('updateBookmark', (updatedKey, value) => {
      const nodeData = this.nodesData.nodeMap[updatedKey]
      if (nodeData) {
        this.$set(nodeData, 'bookmark', value)
      }
    })

    EventBus.$on('updateBaApply', (value) => {
      const gUrlId = value.gUrlId
      const nodeData = this.nodesData.nodeMap[gUrlId]
      if (nodeData) {
        this.$set(nodeData, 'baApply', value)
      }
      if (this.reportData) {
        this.$set(this.reportData, 'baApply', value)
      }
    })

    clearInterval(this.captureProcessingTimer)
    this.captureProcessingTimer = setInterval(() => {
      const data = this.captureProcessing
      if (data) {
        checkCaptureExist(data.sid, data.urlId, data.matchType, data.url)
          .then(result => {
            if (result.imagePath && result.imagePath[this.capturingDevice]) {
              const identifier = this.journeyOptions.matchType === 'exact' ? data._id : data.urlId
              const nodeInfo = this.nodesData.nodeMap[identifier]
              if (nodeInfo) {
                const deviceCapture = result.imagePath[this.capturingDevice]
                const newCaptureDate = getS3CaptureDatetime(deviceCapture) || result.captureDate

                if (deviceCapture && nodeInfo.captureDate !== newCaptureDate) {
                  if (nodeInfo.gUrlId === data.urlId) {
                    this.captureProcessing = null
                    nodeInfo.captureDate = this.reportData.captureDate = result.captureDate
                    this.reportData.realCaptureDate[this.capturingDevice] = newCaptureDate
                    nodeInfo.imagePath[this.capturingDevice] = this.reportData.imagePath[this.capturingDevice] = result.imagePath[this.capturingDevice]
                    nodeInfo.realCaptureUrl[this.capturingDevice] = this.reportData.realCaptureUrl[this.capturingDevice] = result.realCaptureUrl[this.capturingDevice]

                    const currentThumbnail = this.getThumbnailPath(result, this.device)
                    if (currentThumbnail) {
                      nodeInfo.thumbnail = this.reportData.thumbnail = getS3Url(currentThumbnail)
                    }
                  }
                }
              }
            }
          })
          .catch(() => {
            // this.$set(this.captureProcessing, data, false)
            this.captureProcessing = null
          })
      }
    }, 5000)

    this.loadGroupList()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy () {
    clearInterval(this.captureProcessingTimer)
    this.captureProcessingTimer = null
    this.benchmarkData = null
    EventBus.$off('loadPaneData')
    EventBus.$off('loadPaneNoData')
    EventBus.$off('settingGroup')
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    ...mapMutations([
      'setOriginTrendData',
      'setAIReportData',
      'setGlobalNoData',
      'setJourneyOptions'
    ]),
    onCustomTag () {
      this.setJourneyOptions({
        ...this.journeyOptions,
        isCustomNode: !this.journeyOptions.isCustomNode
      })

      EventBus.$emitSticky('loadPaneData')
    },
    resetAIReportData () {
      this.setAIReportData(null)
      if (this.$route.query.aiReportId) {
        const newQuery = { ...this.$route.query }
        delete newQuery.aiReportId
        this.$router.replace({ query: newQuery })
      }
    },
    getBenchmarkData () {
      if (this.domainSubCategory && !this.isCustomNode) {
        const requestData = {
          parentSid: this.domain.parentSid,
          matchType: this.domain.matchType,
          startDate: this.journeyPeriod.start,
          endDate: this.journeyPeriod.end,
          exist: this.dataOption.exist,
          device: this.dataOption.device,
          AICategory2: this.domainSubCategory
        }
        getTrendBenchmark(requestData, true).then(res => {
          this.benchmarkData = res
        })
      }
    },
    getReliability () {
      if (!(this.domain.preSamplingRate < 100 || this.domain.postSamplingRate < 100)) return

      const requestData = {
        sid: this.journeyOptions.sid,
        matchType: this.journeyOptions.matchType,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end
      }

      if (new Date(this.journeyPeriod.start).getTime() >= new Date(this.domain.samplingChangeDate).getTime()) {
        getReliabilityData(requestData).then(res => {
          const result = this.$t('common.samplingReliability.reliabilityRateText', { data: res.reliabilityPercentage.toFixed(1) })
          this.samplingReliabilityText = result
        })
      } else {
        this.samplingReliabilityText = this.$t('common.samplingReliability.reliabilityRateException')
      }
    },
    loadData () {
      EventBus.$emit('onLoading', true)
      getData(this.dataOption, true)
        .then(response => {
          this.resetAIReportData()

          this.nodesData = null
          this.nodesData = response

          this.nodesData.loadedAt = new Date()

          this.nodesDataArray = null

          this.moreDataIndex = 0
          this.thruData = this.thruDataArray = null
          this.thruDataCache = {}
          this.paneScale = 1

          if (response.seqMap['0']) {
            if (!this.trackingData) {
              this.overallSessionData = {
                sessCnt: response.seqMap['0'].seqSessCnt,
                memberSessCnt: response.seqMap['0'].memberSessCnt
              }
            }
            response.seqMap['0'].sampleSeqSessCnt = response.seqMap['0'].seqSessCnt
            EventBus.$emitSticky('loadPaneNoData', false)
          } else {
            EventBus.$emitSticky('loadPaneNoData', true, this.isSampling && !this.isSurvey)
          }

          if (this.isSampling && !this.isSurvey) {
            const originSamplingRate = this.domain.samplingRate || 1
            const sampleCount = this.basicInfo.resample.session // result.sessCnt
            const originCount = this.basicInfo.resample.totalSessCount // result.totalSessCount // this.basicInfo[this.journeyOptions.device]
            const sessionSamplingRate = this.domain.samplingRate / 100
            const resampleCount = sampleCount / (originSamplingRate / 100)
            const reliability = getResampleReliability(originCount, resampleCount)

            if (sampleCount <= 0 || resampleCount <= 0) {
              EventBus.$emitSticky('loadPaneNoData', true, true)
            }

            const origin = {
              ...this.originTrendData,
              totalSessCount: response.totalSessCount,
              resampleCount: resampleCount,
              originSamplingRate,
              sessionSamplingRate: sessionSamplingRate > 0 ? sessionSamplingRate : originSamplingRate,
              reliability: reliability
            }

            this.setOriginTrendData(origin)
            response.seqMap['0'].seqSessCnt = response.totalSessCount
          }

          this.generateNodeDataArray()

          if (!this.isCustomNode) {
            const nodeMap = this.nodesData.nodeMap
            const a = document.createElement('a')
            const journeyOptionsDomain = this.journeyOptions.domain
            let forceDisplaySubdomain = false
            for (let key in nodeMap) {
              if (nodeMap[key]) {
                a.href = nodeMap[key].url
                if (a.hostname !== this.journeyOptions.domain) {
                  const subDomain = a.hostname.replace('.' + this.journeyOptions.domain, '')
                  if (subDomain.split('.')[0] !== 'www') {
                    forceDisplaySubdomain = true
                    break
                  }
                }
              }
            }

            for (let key in nodeMap) {
              if (nodeMap[key]) {
                nodeMap[key].key = [
                  this.journeyOptions.matchType.substr(0, 1),
                  this.journeyOptions.sid,
                  '|', key
                ].join('')
                nodeMap[key].linkUrl = simpleUrl(nodeMap[key].url, this.journeyOptions.matchType)

                a.href = nodeMap[key].url

                nodeMap[key].linkHost = getDomainForView(a.host)

                nodeMap[key].linkPath = a.pathname
                if (this.journeyOptions.matchType === 'exact') {
                  const pathStartIdx = a.href.indexOf(a.host) + a.host.length
                  nodeMap[key].linkPath = a.href.slice(pathStartIdx)
                }
                nodeMap[key].linkPath = decodeURI(nodeMap[key].linkPath)
                nodeMap[key].linkProtocol = a.protocol

                if (a.hostname !== journeyOptionsDomain) {
                  let subDomain = a.hostname.replace('.' + journeyOptionsDomain, '')
                  if (forceDisplaySubdomain || subDomain.split('.')[0] !== 'www') {
                    nodeMap[key].subDomain = subDomain
                  }
                }
              }
            }
          }

          EventBus.$emit('onLoading', false)
          if (this.noData) {
            EventBus.$emitSticky('loadPaneNoData', false)
          }

          this.$nextTick(() => {
            this.unsetPaneDraggable()
            this.setPaneDraggable()

            this.onResize()
          })
        })
        .catch((e) => {
          EventBus.$emit('onError')
          this.apiLoading = false
        })
    },
    loadMoreData () {
      this.partiallyLoading = true
      getMoreData({
        ...this.dataOption,
        trendMap: this.nodesData.trendMap,
        targetSequence: this.nodesDataArray.length
      }, true)
        .then(response => {
          const result = response
          this.addMoreData(result.seqMap)
          this.partiallyLoading = false

          setTimeout(() => {
            this.setPaneMinPosition()
            this.panePosition = [this._paneBounds[2], this.panePosition[1]]
          }, 100)
        })
        .catch(() => {
          EventBus.$emit('onError')
          this.apiLoading = false
        })
    },
    loadThruTrafficData (sequenceIndex, nodeIndex) {
      const cacheKey = sequenceIndex + '-' + nodeIndex
      if (this.thruDataCache[cacheKey]) {
        this.thruData = this.thruDataCache[cacheKey]
        this.generateThruDataArray()
      } else {
        const node = this.nodesDataArray[sequenceIndex].nodesArray[nodeIndex]
        const id = isNaN(node._id) ? node._id : parseInt(node._id)
        const nodeInfo = this.nodesData.nodeMap[id]
        this.partiallyLoading = true
        const params = {
          ...this.dataOption,
          thruNodeSeq: sequenceIndex,
          thruNodeId: id,
          trendMap: this.nodesData.trendMap
        }

        getThruTrafficData(params, true)
          .then(response => {
            response._sequenceIndex = sequenceIndex
            response._nodeIndex = nodeIndex
            response._urlId = id
            if (this.isCustomNode) {
              response.customId = id
              response.customValue = nodeInfo.customValue
            }

            this.thruData = this.thruDataCache[cacheKey] = response

            this.generateThruDataArray()

            this.partiallyLoading = false
          })
          .catch((e) => {
            EventBus.$emit('onError')
            this.apiLoading = false
          })
      }
    },
    removeThruTrafficData () {
      this.thruData = this.thruDataArray = null
    },
    loadNodeDetailData (id, gUrlId, url, onLoad) {
      this.partiallyLoading = true
      const params = {
        ...this.dataOption,
        gUrlId,
        url
      }
      if (this.isCustomNode) {
        params['nodeKey'] = isNaN(id) ? id : Number(id)
      }
      getNodeDetail(params, true)
        .then(response => {
          const nodeInfo = this.nodesData.nodeMap[id]
          for (let key in response) {
            nodeInfo[key] = response[key]
          }
          nodeInfo._detailAdded = true
          onLoad && onLoad()
          this.partiallyLoading = false
        })
        .catch(() => {
          EventBus.$emit('onError')
          this.apiLoading = false
        })
    },
    loadEtcUrlList (sequenceIndex, highlightedSequenceIndex, highlightedNodeIndex, onLoad) {
      const payload = {
        ...this.dataOption,
        sequence: sequenceIndex,
        trendMap: this.nodesData.trendMap
      }
      this.partiallyLoading = true
      if (this.thruData) {
        const id = this.nodesDataArray[highlightedSequenceIndex].nodesArray[highlightedNodeIndex]._id
        payload.thruNodeSeq = highlightedSequenceIndex
        payload.thruNodeId = isNaN(id) ? id : parseInt(id)
      }
      getEtcUrls(payload, true)
        .then(response => {
          onLoad && onLoad(response || {})
          this.partiallyLoading = false
        })
        .catch(() => {
          EventBus.$emit('onError')
          this.apiLoading = false
        })
    },

    addDataArray (sequenceIndex, fromData, toArray = [], isNextArray) {
      sequenceIndex = parseInt(sequenceIndex)

      const targetTrendMap = this.nodesData.trendMap[isNextArray ? sequenceIndex + 1 : sequenceIndex]
      let etcNodeData = null

      if (toArray.length) {
        toArray.forEach((nodeData) => {
          if (nodeData.next && !isEmptyObject(nodeData.next)) {
            nodeData.nextArray = this.addDataArray(sequenceIndex, nodeData.next, [], true)
          }
        })
      } else {
        for (let id in fromData) {
          fromData[id]._id = id
          if (fromData[id].thruTargetNext) {
            fromData[id].next = fromData[id].thruTargetNext
            delete fromData[id].thruTargetNext
          }
          if (fromData[id].next && !isEmptyObject(fromData[id].next)) {
            fromData[id].nextArray = this.addDataArray(sequenceIndex, fromData[id].next, [], true)
          }
          if (id === '-1') {
            etcNodeData = fromData[id]
          } else {
            toArray[targetTrendMap.indexOf(isNaN(id) ? id : parseInt(id))] = fromData[id]
          }
        }

        if (etcNodeData) {
          toArray.push(etcNodeData)
        }
      }
      return toArray.filter(data => !!data)
    },
    getMaxSeq () {
      const width = document.body.clientWidth
      const maxWidthList = this.shouldReserveStartContextSpace ? [1635, 1955, 2155] : [1400, 1720, 1920]
      switch (true) {
        case (width <= maxWidthList[0]):
          return 3
        case (width <= maxWidthList[1]):
          return 4
        case (width <= maxWidthList[2]):
          return 5
        default:
          return 6
      }
    },
    generateNodeDataArray (fromIndex) {
      const seqMap = this.nodesData.seqMap
      const dataArray = this.nodesDataArray || []
      const maxSeq = fromIndex ? 6 : this.getMaxSeq()
      const numSequences = Math.min(Math.max.apply(null, Object.keys(seqMap)) + 1, maxSeq)
      for (let sequenceIndex = fromIndex || 0; sequenceIndex < numSequences; sequenceIndex++) {
        const sequence = seqMap[sequenceIndex]
        if (sequenceIndex !== 0 && fromIndex !== sequenceIndex) {
          sequence.sampleSeqSessCnt = sequence.seqSessCnt
        }

        if (this.isSampling && !this.isSurvey && sequenceIndex) {
          if (sequenceIndex !== fromIndex) {
            sequence.seqSessCnt = sequence.seqSessCnt / this.originTrendData.sessionSamplingRate
          }
        }

        if (sequence.nodes['-1']) {
          sequence.nodes['-1']._seqIndex = sequenceIndex
          sequence.nodes['-1']._seqSessCnt = sequence.seqSessCnt
        }
        sequence.nodesArray = this.addDataArray(sequenceIndex, sequence.nodes, sequence.nodesArray)
        sequence.nodesArray.forEach((nodeData) => {
          if (this.isSampling && !this.isSurvey) {
            const rate = this.originTrendData.sessionSamplingRate

            if (!nodeData.resampleSessCnt) {
              nodeData.resampleSessCnt = nodeData.sessCnt / rate
              nodeData.resampleCnt = nodeData.cnt / rate
              nodeData.resampleDropCnt = nodeData.dropCnt / rate
            }
          }
          nodeData.nextArray && nodeData.nextArray.forEach((nextData) => {
            const nextNodeData = seqMap[sequenceIndex + 1].nodes[nextData._id]
            if (nextNodeData) {
              nextNodeData._realInSessCnt = 0
            }
          })
        })
        sequence.nodesArray.forEach((nodeData) => {
          if (nodeData.nextArray) {
            nodeData._realOutSessCnt = nodeData.dropCnt
            nodeData.nextArray && nodeData.nextArray.forEach((nextData) => {
              nodeData._realOutSessCnt += nextData.sessCnt
              const nextNodeData = seqMap[sequenceIndex + 1].nodes[nextData._id]
              if (nextNodeData) {
                nextNodeData._realInSessCnt += nextData.sessCnt
              }
            })
            nodeData._realOutSessCnt = Math.max(nodeData._realOutSessCnt, nodeData.sessCnt)
          } else {
            nodeData._realOutSessCnt = nodeData.sessCnt
          }
        })
        dataArray[sequenceIndex] = sequence
      }
      this.nodesDataArray = dataArray
      this.setValueRange()
    },
    generateThruDataArray () {
      const seqMap = this.thruData.seqMap
      const dataArray = []
      for (let sequenceIndex in seqMap) {
        const sequence = seqMap[sequenceIndex]
        sequence.nodesArray = this.addDataArray(sequenceIndex, sequence.nodes, [])
        dataArray[sequenceIndex] = sequence
      }
      this.thruDataArray = dataArray
    },
    setValueRange () {
      for (let key in this.valueRange) {
        this.valueRange[key] = []
      }
      const nextSessionRange = []
      const firstNodeSessionCount = this.nodesDataArray[0].seqSessCnt
      this.nodesDataArray.forEach((sequence) => {
        sequence.nodesArray.forEach((nodeData) => {
          if (!nodeData.rollback) {
            const nodeDropOff = nodeData.dropCnt ? nodeData.dropCnt / nodeData.sessCnt * 100 : 0
            nodeData.dropOff = nodeDropOff > 100 ? 100 : nodeDropOff
            nodeData.rollback = nodeData.rollCnt ? nodeData.rollCnt / nodeData.sessCnt * 100 : 0
            nodeData.repeat = nodeData.reloadCnt ? nodeData.reloadCnt / nodeData.sessCnt * 100 : 0
            nodeData.sessTime = nodeData.sessTime / 1000
          }
          if (nodeData._id !== '-1') {
            this.valueRange.rollback.push(nodeData.rollback)
            this.valueRange.dropOff.push(nodeData.dropOff > 100 ? 100 : nodeData.dropOff)
            this.valueRange.sessionTime.push(nodeData.sessTime)
            this.valueRange.repeat.push(nodeData.repeat)
          }

          nodeData.nextArray && nodeData.nextArray.forEach((nextData) => {
            nextSessionRange.push(nextData.sessCnt / firstNodeSessionCount)
          })
        })
      })
      for (let key in this.valueRange) {
        this.valueRange[key].sort(sortByValue)
      }

      nextSessionRange.sort(sortByValue).reverse()
      const numNextSessionRange = nextSessionRange.length
      this.nodesDataArray.forEach((sequence, sequenceIndex) => {
        sequence.nodesArray.forEach((nodeData, nodeIndex) => {
          nodeData.nextArray && nodeData.nextArray.forEach((nextData, nextIndex) => {
            const rank = nextSessionRange.indexOf(nextData.sessCnt / firstNodeSessionCount)
            nextData._rankRate = Math.round(rank / numNextSessionRange * 100)
          })
        })
      })
    },
    expandSequence () {
      const startIndex = this.nodesDataArray.length - 1
      this.generateNodeDataArray(startIndex)
      this.moreDataIndex = startIndex
    },
    addMoreData (data) {
      const startIndex = this.nodesDataArray.length - 1
      for (let index in data) {
        if (parseInt(index) === startIndex) {
          this.nodesData.seqMap[index].nodesArray.forEach((nodeData) => {
            nodeData.next = data[index].nodes[nodeData._id].next
          })
        } else {
          this.nodesData.seqMap[index] = data[index]
        }
      }
      this.generateNodeDataArray(startIndex)
      this.moreDataIndex = startIndex
    },

    showReport (sequenceIndex, nodeIndex, highlightedSequenceIndex, highlightedNodeIndex) {
      if (!this.reportShowing) {
        const nodeData = this.nodesDataArray[sequenceIndex].nodesArray[nodeIndex]
        const nodeInfo = this.nodesData.nodeMap[nodeData._id] || {}
        if (nodeData._id !== '-1') {
          if (!nodeInfo._detailAdded) {
            this.loadNodeDetailData(nodeData._id, nodeData.gUrlId, nodeInfo.url, () => {
              this.showReport(sequenceIndex, nodeIndex, highlightedSequenceIndex, highlightedNodeIndex)
            })
            return
          }
          nodeData.referMap = nodeData.referMap || null
        } else if (nodeData._id === '-1') {
          if (!nodeData._urlListCache) {
            nodeData._urlListCache = {}
          }
          const urlListCacheKey = (highlightedSequenceIndex || -1) + '-' + (highlightedNodeIndex || -1)
          if (nodeData._urlListCache[urlListCacheKey]) {
            nodeData._urlList = nodeData._urlListCache[urlListCacheKey]
          } else {
            this.loadEtcUrlList(sequenceIndex, highlightedSequenceIndex, highlightedNodeIndex, (urlList) => {
              nodeData._urlList = nodeData._urlListCache[urlListCacheKey] = urlList
              this.showReport(sequenceIndex, nodeIndex, highlightedSequenceIndex, highlightedNodeIndex)
            })
            return
          }
        }

        this.reportShowing = true
        this.nodeReporting = true

        const reportData = {
          isTrend: true,
          sequenceIndex: sequenceIndex,
          nodeId: isNaN(nodeData._id) ? nodeData._id : parseInt(nodeData._id),
          ...nodeData,
          ...nodeInfo
        }

        if (highlightedSequenceIndex > -1 && this.thruDataArray) {
          const nodeId = reportData._id
          if (this.thruDataArray[sequenceIndex] && this.thruDataArray[sequenceIndex].nodes[nodeId]) {
            reportData.thruData = this.thruDataArray[sequenceIndex].nodes[nodeId]
            const thruDataDropOff = reportData.thruData.dropCnt ? reportData.thruData.dropCnt / reportData.sessCnt * 100 : 0
            reportData.thruData.dropOff = thruDataDropOff > 100 ? 100 : thruDataDropOff
          } else {
            reportData.thruData = {}
          }
          const highlightedNodeId = this.nodesDataArray[highlightedSequenceIndex].nodesArray[highlightedNodeIndex]._id
          reportData.highlightedSequenceIndex = highlightedSequenceIndex
          reportData.highlightedNodeIndex = highlightedNodeIndex
          reportData.highlightedNodeInfo = this.nodesData.nodeMap[highlightedNodeId]
        }

        reportData.realCaptureDate = reportData.captureDate
        if (reportData.imagePath && reportData.imagePath[this.journeyOptions.device]) {
          const deviceCapture = reportData.imagePath[this.journeyOptions.device]
          reportData.realCaptureDate = getS3CaptureDatetime(deviceCapture) || reportData.captureDate
          reportData.thumbnail = getS3Url(deviceCapture)
        }

        reportData.imagePath = reportData.imagePath || {}
        reportData.realCaptureDate = {
          desktop: getS3CaptureDatetime(reportData.imagePath['desktop']) || reportData.captureDate,
          tablet: getS3CaptureDatetime(reportData.imagePath['tablet']) || reportData.captureDate,
          phone: getS3CaptureDatetime(reportData.imagePath['phone']) || reportData.captureDate
        }

        const currentThumbnail = this.getThumbnailPath(reportData, this.device)
        if (currentThumbnail) {
          reportData.thumbnail = getS3Url(currentThumbnail)
        }

        if (!this.defaultThumbnail && reportData.thumbnail) {
          this.defaultThumbnail = reportData.thumbnail
        }

        this.reportData = reportData

        const layout = nodeData._layout
        this._formerPosition = this.panePosition.slice()
        let left = (layout.left + (layout.width / 2) + (this.panePadding[3] - (this.panePadding[3] - 51)) + this.paneExtraPadding[3]) * this.paneScale
        let top = (layout.top + (layout.height / 2) + 95 + this.panePadding[0] + this.paneExtraPadding[0]) * this.paneScale
        left = -(left - (this.paneWrapSize[0] / 2) + (430 / 2))
        top = -(top - (this.paneWrapSize[1] / 2))
        this.panePosition = [left, top]
        this.setPaneOverflowPosition()
        if (this._paneBounds[2] > left) {
          this.panePosition[0] = Math.max(left, this._paneBounds[2] - 430)
        }
      }
    },
    hideReport () {
      if (this.reportShowing) {
        this.reportShowing = false
        this.panePosition = this._formerPosition
      }
    },
    onNodeReportLeave () {
      this.nodeReporting = false
    },

    onCapture (pageData, device) {
      this.capturingDevice = device
      console.log(pageData)
      this.capturingPage = pageData
    },
    onCaptureProcessing (pageKey) {
      // this.$set(this.captureProcessing, pageKey, true)
      this.captureProcessing = pageKey
    },

    loadGroupList () {
      return new Promise(resolve => {
        getGroupList({
          sid: this.sid
        }, true).then(result => {
          this.groupItems = {}
          for (let group of result) {
            if (group.usableStatus !== 'PENDING') {
              this.groupItems[group.groupId] = group.groupName
            }
          }
          resolve()
        })
      })
    },
    setGrouping (value) {
      if (this.grouping !== value) {
        this.grouping = value
      }
      EventBus.$emit('group-state', this.grouping)
    },
    onGroupChange (value) {
      if (this.group !== value) {
        this.group = value
      }
      this.setGrouping(true)
    },
    onSettingGroup () {
      this.movingGroup = false
      this.$router.push({ name: 'group', query: { sid: this.sid } })
    },

    onDownloadExcel () {
      let fileName = this.journeyOptions.domain + '_Trend'
      let excel = new Excel()
      const isSampling = this.domain.preSamplingRate < 100 || this.domain.postSamplingRate < 100
      this.apiLoading = true
      excel.setTrendInfo(this.thruData ? {
        sequence: this.thruData._sequenceIndex + 1,
        rank: this.thruData._nodeIndex + 1,
        url: this.nodesData.nodeMap[this.thruData._urlId].linkUrl,
        customId: this.thruData.customId,
        customValue: this.thruData.customValue
      } : null, isSampling, this.originTrendData).then(() => {
        excel.setTrendData(this.nodesDataArray, this.nodesData.nodeMap, this.thruData, this.nodesData.trendMap, isSampling, this.originTrendData)
          .then(() => {
            excel.download(fileName)
            this.apiLoading = false
          })
      }).catch(() => {
        this.apiLoading = false
      })
    },

    setNodeWidth (value) {
      this.nodeWidth = value
      this.setPaneMinPosition()
    },
    setNodeHeight (value) {
      this.nodeHeight = value
      this.setPaneMinPosition()
    },
    setConnectionOption (value) {
      this.connectionStrength = value
    },
    setVisibleOption (name) {
      this.visibleOptions[name] = !this.visibleOptions[name]
    },
    setPaneDraggable () {
      const paneWrap = this.$refs.paneWrap
      if (paneWrap) {
        paneWrap.addEventListener('mousedown', this.onPointerDown)
        paneWrap.addEventListener('touchstart', this.onPointerDown)
        paneWrap.addEventListener(wheelEventName, this.onWheel, { passive: false })
      }
    },
    unsetPaneDraggable () {
      const paneWrap = this.$refs.paneWrap
      if (paneWrap) {
        paneWrap.removeEventListener('mousedown', this.onPointerDown)
        paneWrap.removeEventListener('touchstart', this.onPointerDown)
        paneWrap.removeEventListener(wheelEventName, this.onWheel, { passive: false })
      }
    },
    onWheel (e) {
      const deltaX = e.deltaX !== undefined ? e.deltaX : e.wheelDeltaX || 0
      const deltaY = e.deltaY !== undefined ? e.deltaY : e.wheelDeltaY !== undefined ? e.wheelDeltaY : e.detail || e.wheelDelta * -1
      this.setPaneMinPosition()
      const left = Math.min(this._paneBounds[0], Math.max(this._paneBounds[2], this.panePosition[0] - deltaX))
      const top = Math.min(this._paneBounds[1], Math.max(this._paneBounds[3], this.panePosition[1] - deltaY))
      cancelAnimationFrame(this._debouncer)
      this._debouncer = requestAnimationFrame(() => {
        this.setPanePositionWithoutTransition(left, top)
      })
      e.preventDefault()
    },
    onPointerDown (e) {
      if (!e.which || e.which === 1) {
        this._downPoint = getEventPoint(e)
        this._downPosition = this.panePosition
        this.setPaneMinPosition()
        this._dragActivated = false
        document.addEventListener('mousemove', this.onPointerMove)
        document.addEventListener('touchmove', this.onPointerMove, { passive: false })
        document.addEventListener('mouseup', this.onPointerUp)
        document.addEventListener('touchend', this.onPointerUp)
      }
    },
    onPointerMove (e) {
      const point = getEventPoint(e)
      if (!this._dragActivated && (Math.abs(this._downPoint[0] - point[0]) > 2 || Math.abs(this._downPoint[0] - point[0]) > 2)) {
        this._dragActivated = true
        document.body.classList.add('dragging')
      }
      if (this._dragActivated) {
        let left = this._downPosition[0] + (point[0] - this._downPoint[0])
        let top = this._downPosition[1] + (point[1] - this._downPoint[1])
        const overflowDivideFactor = 3
        if (left > this._paneBounds[0]) {
          left = this._paneBounds[0] + (left - this._paneBounds[0]) / overflowDivideFactor
        } else if (this._paneBounds[2] > left) {
          left = this._paneBounds[2] + (left - this._paneBounds[2]) / overflowDivideFactor
        }
        if (top > this._paneBounds[1]) {
          top = this._paneBounds[1] + (top - this._paneBounds[1]) / overflowDivideFactor
        } else if (this._paneBounds[3] > top) {
          top = this._paneBounds[3] + (top - this._paneBounds[3]) / overflowDivideFactor
        }
        cancelAnimationFrame(this._debouncer)
        this._debouncer = requestAnimationFrame(() => {
          this.panePosition = [parseInt(left), parseInt(top)]
        })
      }
      e.preventDefault()
    },
    onPointerUp () {
      if (this._dragActivated) {
        cancelAnimationFrame(this._debouncer)
        this.setPaneOverflowPosition()
        document.body.classList.remove('dragging')
      }
      document.removeEventListener('mousemove', this.onPointerMove)
      document.removeEventListener('touchmove', this.onPointerMove)
      document.removeEventListener('mouseup', this.onPointerUp)
      document.removeEventListener('touchend', this.onPointerUp)
    },
    onPaneScaleChange (scale) {
      if (this.paneScale !== scale) {
        const relatedScale = scale / this.paneScale
        const position = [
          (this.panePosition[0] - this.paneWrapSize[0] * 0.5) * relatedScale + this.paneWrapSize[0] * 0.5,
          (this.panePosition[1] - this.paneWrapSize[1] * 0.5) * relatedScale + this.paneWrapSize[1] * 0.5
        ]
        this.panePosition = position
        this.paneScale = scale
        this.setPaneOverflowPosition()
      }
    },
    onPaneScaleFit () {
      const pane = this.$refs.pane
      const paneOffsetWidth = pane.offsetWidth
      const paneOffsetHeight = pane.offsetHeight
      const paneHorizontalPadding = this.panePadding[1] + this.panePadding[3]
      const paneVerticalPadding = this.panePadding[0] + this.panePadding[2]
      let paneContentWidth = paneOffsetWidth - paneHorizontalPadding - this.paneExtraPadding[1] - this.paneExtraPadding[3]
      let paneContentHeight = paneOffsetHeight - paneVerticalPadding - this.paneExtraPadding[0] - this.paneExtraPadding[2]
      let scale = Math.min(this.paneWrapSize[0] / paneContentWidth, this.paneWrapSize[1] / paneContentHeight)

      paneContentWidth += paneHorizontalPadding * 1 / scale
      paneContentHeight += paneVerticalPadding * 1 / scale
      scale = Math.max(0.25, Math.min(this.paneWrapSize[0] / paneContentWidth, this.paneWrapSize[1] / paneContentHeight))

      const scaledWidth = paneOffsetWidth * scale
      const scaledHeight = paneOffsetHeight * scale
      this.panePosition = [
        -(scaledWidth - this.paneWrapSize[0]) / 2 + (52 / 4 * 1 / scale),
        -(scaledHeight - this.paneWrapSize[1]) / 2
      ]
      this.paneScale = scale

      this.setPaneOverflowPosition()
    },
    getPaneWrapSize () {
      const paneWrap = this.$refs.paneWrap
      this.paneWrapSize = [paneWrap.offsetWidth, paneWrap.offsetHeight]
    },
    setPaneMinPosition () {
      const pane = this.$refs.pane
      const [ paneWrapWidth, paneWrapHeight ] = this.paneWrapSize
      const paneWidth = pane.offsetWidth * this.paneScale
      const paneHeight = pane.offsetHeight * this.paneScale
      this._paneBounds = [
        Math.max(0, (paneWrapWidth - paneWidth) / 2),
        Math.max(0, (paneWrapHeight - paneHeight) / 2),
        paneWrapWidth - pane.offsetWidth * this.paneScale,
        paneWrapHeight - pane.offsetHeight * this.paneScale
      ]
      this._paneBounds[2] = Math.min(this._paneBounds[0], this._paneBounds[2])
      this._paneBounds[3] = Math.min(this._paneBounds[1], this._paneBounds[3])
    },
    setPaneOverflowPosition (withoutTransition) {
      this.setPaneMinPosition()
      const [ paneX, paneY ] = this.panePosition
      const [ minX, minY, maxX, maxY ] = this._paneBounds
      const x = paneX > minX ? minX : maxX > paneX ? maxX : paneX
      const y = paneY > minY ? minY : maxY > paneY ? maxY : paneY
      if (withoutTransition) {
        this.setPanePositionWithoutTransition(x, y)
      } else {
        this.panePosition = [x, y]
      }
    },
    setPanePositionWithoutTransition (x, y) {
      const pane = this.$refs.pane
      if (pane) {
        clearTimeout(this._restoreTransitionTimer)
        pane.classList.add('no-transition')
        this.panePosition = [x, y]
        this._restoreTransitionTimer = setTimeout(() => {
          pane.classList.remove('no-transition')
        }, 200)
      }
    },

    onResize (withOverflowPosition) {
      if (this.$refs.paneWrap) {
        this.getPaneWrapSize()
        withOverflowPosition && this.setPaneOverflowPosition()
        this.setPanePositionWithoutTransition.apply(null, this.panePosition)
      }
    },

    onBlockerClick (e) {
      if (this.reportShowing) {
        this.hideReport()
      }
    },

    getThumbnailPath (data, device) {
      let path = null
      if (data.imagePath) {
        const { imagePath } = data
        path = imagePath[device] || imagePath.desktop || imagePath.phone || imagePath.tablet
      }
      return path
    },
    changeCategory (value) {
      this.domainSubCategory = value
      this.getBenchmarkData()
      this.isCategorySelectorOpened = false
    },
    showTrackingLayer (sequenceIndex, nodeIndex) {
      const nodeData = {
        isTrend: true,
        isForStartNode: nodeIndex === -1
      }

      if (this.trackingData) {
        nodeData.trackingData = this.trackingData
      }

      if (nodeIndex !== -1) {
        const node = this.nodesDataArray[sequenceIndex].nodesArray[nodeIndex]
        const isEtcNode = node._id === '-1'
        nodeData.isForEtcNodes = isEtcNode
        nodeData.trendMap = this.nodesData.trendMap
        nodeData.sequence = sequenceIndex
        if (!isEtcNode) {
          nodeData.gUrlId = node.gUrlId
          nodeData.nodeKey = isNaN(node._id) ? node._id : Number(node._id)
        }
      }
      this.trackingNodeData = nodeData
      this.trackingLayerShowing = true
    },
    setTracking (data) {
      if (data.init) {
        this.stopTracking()
      } else {
        this.trackingData = data
        this.trackingLayerShowing = false
        EventBus.$emitSticky('loadPaneData')
      }
    },
    stopTracking () {
      this.trackingData = null
      this.trackingNodeData = null
      EventBus.$emitSticky('loadPaneData')
    },
    onClickAiReport () {
      if (!this.ableToShowAiReport) return
      EventBus.$emit('setSegmentPayment')
    }
  }
}
</script>

<style lang="scss">
body.dragging {
  cursor: -webkit-grabbing;
  cursor: grabbing;
}
</style>
<style lang="scss" scoped>
.ba-trend {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .ba-pane-control {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 8;
    width: 52px;
    height: 100%;
  }

  .pane-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: #e5e5e1;
    overflow: hidden;
    cursor: -webkit-grab;
    cursor: grab;
    touch-action: none;

    body.dragging & {
      user-select: none;
      pointer-events: none;
    }

    &.node-reporting {
      .pane {
        transition-timing-function: ease;
      }
    }
  }

  .pane {
    position: relative;
    left: 0;
    top: 0;
    min-width: 100%;
    min-height: 100%;
    box-sizing: border-box;
    background-color: #f4f4f1;
    display: inline-block;
    transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    transform-origin: 0 0;
    will-change: transform;

    &.no-transition,
    body.dragging & {
      transition: none;
    }
  }

  .blocker {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    z-index: 9;
  }

  .node-report {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
    width: 444px;
    height: 100%;
    box-shadow: 0 2px 20px -2px rgba(0, 0, 0, 0.08);
    border-left: 1px solid #c9c9c6;
    background-color: #ffffff;
    transition: transform 0.5s ease;

    &.node-report-enter,
    &.node-report-leave-to {
      transform: translate(110%, 0);
    }

    .ba-node-report,
    .ba-node-report-etc {
      overflow: auto;
      height: 100%;
      position: relative;
      z-index: 3;
    }

    .close-report {
      position: absolute;
      z-index: 2;
      top: 50%;
      left: -20px;
      width: 20px;
      height: 50px;
      padding: 17px 0;
      border: 1px solid #c9c9c6;
      border-right: none;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      transform: translateY(-50%);
      background: #ffffff;

      &::after,
      &::before {
        content: "";
        width: 6px;
        height: 6px;
        border: 1px solid #c9c9c6;
        position: absolute;
        right: 0;
        border-left: none;
      }

      &::after {
        border-top: none;
        border-radius: 0 0 5px 0;
        top: -7px;
      }

      &::before {
        border-bottom: none;
        border-radius: 0 5px 0 0;
        bottom: -7px;
      }

      .icon {
        &::after,
        &::before{
          content: "";
          width: 6px;
          height: 6px;
          border: 2px solid #ffffff;
          position: absolute;
          right: -2px;
          border-left: none;
        }

        &::after {
          border-top: none;
          border-radius: 0 0 7px 0;
          top: -6px;
        }

        &::before {
          border-bottom: none;
          border-radius: 0 7px 0 0;
          bottom: -6px;
        }
      }
    }
  }

  .reliability {
    position: absolute;
    z-index: 9;
    bottom: 43.5px;
    right: 25px;
    vertical-align: middle;
    height: 28px;
    border-radius: 5px;

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.3s ease;
    }
    &.fade-enter,
    &.fade-leave-to{
      opacity: 0;
    }

    i {
      margin: 4px 0 0 5px;
      float:left;
    }

    span {
      color: #767676;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 28px;
      padding: 0 0px 0 5px;
      vertical-align: middle;
    }
  }

  .pane-base-info {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    z-index: 8;

    color: #2f2f2f;
    font-weight: 400;
    font-size: 12px;
    line-height: 1;
    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.5s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }

    .sampling-reliability {
      display: flex;
      align-items: center;
      gap: 5px;
      .icon-wrap {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #FFB427;
        box-sizing: border-box;
      }
    }
    .pane-date {
      padding-left: 25px;
    }
  }

  .ai-report-button {
    position: absolute;
    top: 27px;
    right: 24px;
    display: flex;
    height: 32px;
    padding: 4px 10px 4px 6px;
    margin-left: 21px;
    align-items: center;
    gap: 4px;
    border-radius: 5px;
    background-color: #fff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 3px 4px -2px rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    color: #555;
    .text-contents {
      min-width: 41px;
      font-size: 13px;
      font-weight: 500;
      text-align: center;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }
    &:disabled {
      opacity: 0.3;
      pointer-events: none;
    }
  }

  .grouping-pane {
    position: absolute;
    bottom: 20px;
    right: 20px;
    z-index: 11;

    &.grouping-leave-active,
    &.grouping-enter-active {
      transition: right 0.5s ease;
      right: 20px;
    }

    &.grouping-enter,
    &.grouping-leave-to {
      right: -250px;
    }
  }

  .pane-no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.5s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }

    h3 {
      font-size: 20px;
      font-weight: 500;
      color: #444444;
      margin: 10px 0 20px 0;
    }

    p {
      font-size: 14px;
      line-height: 1.57;
      color: #666666;
      margin-bottom: 50px;
    }
  }

  .partially-loading {
    &.fade-leave-active,
    &.fade-enter-active {
      transition: opacity 0.3s ease;
      opacity: 1;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
    }
  }
}
</style>
